import React, { useState, useEffect } from 'react';
import { Heart, AlertCircle, CheckCircle, Search, Building, Eye, Trash2, Check, X } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import AdminGlobalLayout from '../../components/AdminGlobalLayout';
import AdminDataTable from '../../components/AdminDataTable';

interface PrayerRequest {
  id: string;
  name: string;
  request: string;
  is_public: boolean;
  is_anonymous: boolean;
  created_at: string;
  user_id: string;
  igreja_id: string;
  igreja_nome?: string;
  status?: 'pending' | 'approved' | 'rejected';
}

interface Igreja {
  id: string;
  nome: string;
  cidade: string;
  estado: string;
}

export default function AdminPrayerRequests() {
  const [prayerRequests, setPrayerRequests] = useState<PrayerRequest[]>([]);
  const [igrejas, setIgrejas] = useState<Igreja[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [viewingRequest, setViewingRequest] = useState<PrayerRequest | null>(null);
  const [requestToDelete, setRequestToDelete] = useState<PrayerRequest | null>(null);
  
  // Table state
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string | null>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredRequests, setFilteredRequests] = useState<PrayerRequest[]>([]);
  const [selectedIgreja, setSelectedIgreja] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    fetchPrayerRequests();
    fetchIgrejas();
  }, []);

  useEffect(() => {
    // Filter and sort prayer requests
    let filtered = [...prayerRequests];
    
    // Apply igreja filter
    if (selectedIgreja) {
      filtered = filtered.filter(request => request.igreja_id === selectedIgreja);
    }
    
    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(request => request.status === statusFilter);
    }
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(request => 
        request.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.request.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.igreja_nome?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply sorting
    if (sortField && sortOrder) {
      filtered.sort((a, b) => {
        let aValue, bValue;
        
        if (sortField === 'igreja_nome') {
          aValue = a.igreja_nome || '';
          bValue = b.igreja_nome || '';
        } else {
          aValue = a[sortField as keyof PrayerRequest];
          bValue = b[sortField as keyof PrayerRequest];
        }
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortOrder === 'asc' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        return 0;
      });
    }
    
    setFilteredRequests(filtered);
  }, [prayerRequests, searchTerm, sortField, sortOrder, selectedIgreja, statusFilter]);

  async function fetchPrayerRequests() {
    try {
      setLoading(true);
      
      // Fetch prayer requests
      const { data: requestsData, error: requestsError } = await supabase
        .from('prayer_requests')
        .select('*');
      
      if (requestsError) throw requestsError;
      
      // Fetch igrejas to get names
      const { data: igrejasData, error: igrejasError } = await supabase
        .from('igrejas')
        .select('id, nome');
      
      if (igrejasError) throw igrejasError;
      
      // Map igreja names to prayer requests
      const requestsWithIgrejaNames = requestsData?.map(request => {
        const igreja = igrejasData?.find(i => i.id === request.igreja_id);
        return {
          ...request,
          igreja_nome: igreja?.nome || 'Sem igreja',
          // Add a status field (this would normally come from the database)
          status: request.is_public ? 'approved' : 'pending'
        };
      }) || [];
      
      setPrayerRequests(requestsWithIgrejaNames);
    } catch (error: any) {
      setError(error.message || 'Erro ao buscar pedidos de oração');
    } finally {
      setLoading(false);
    }
  }

  async function fetchIgrejas() {
    try {
      const { data, error } = await supabase
        .from('igrejas')
        .select('id, nome, cidade, estado')
        .order('nome');

      if (error) throw error;
      setIgrejas(data || []);
    } catch (error: any) {
      setError(error.message || 'Erro ao buscar igrejas');
    }
  }

  const handleApprove = async (request: PrayerRequest) => {
    try {
      setError('');
      setSuccess('');
      
      // Update prayer request to be public
      const { error } = await supabase
        .from('prayer_requests')
        .update({ is_public: true })
        .eq('id', request.id);
      
      if (error) throw error;
      
      setSuccess('Pedido de oração aprovado com sucesso!');
      
      // Refresh data
      fetchPrayerRequests();
    } catch (error: any) {
      setError(error.message || 'Erro ao aprovar pedido de oração');
    }
  };

  const handleReject = async (request: PrayerRequest) => {
    try {
      setError('');
      setSuccess('');
      
      // Update prayer request to be not public
      const { error } = await supabase
        .from('prayer_requests')
        .update({ is_public: false })
        .eq('id', request.id);
      
      if (error) throw error;
      
      setSuccess('Pedido de oração rejeitado com sucesso!');
      
      // Refresh data
      fetchPrayerRequests();
    } catch (error: any) {
      setError(error.message || 'Erro ao rejeitar pedido de oração');
    }
  };

  const handleDelete = async () => {
    if (!requestToDelete) return;
    
    try {
      setError('');
      setSuccess('');
      
      // Delete prayer request
      const { error } = await supabase
        .from('prayer_requests')
        .delete()
        .eq('id', requestToDelete.id);
      
      if (error) throw error;
      
      setSuccess('Pedido de oração excluído com sucesso!');
      setIsDeleteModalOpen(false);
      setRequestToDelete(null);
      
      // Refresh data
      fetchPrayerRequests();
    } catch (error: any) {
      setError(error.message || 'Erro ao excluir pedido de oração');
    }
  };

  const handleSortingChange = (field: string, order: 'asc' | 'desc') => {
    setSortField(field);
    setSortOrder(order);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const columns = [
    {
      header: 'Nome',
      accessorKey: 'name',
      enableSorting: true,
      cell: (info: { row: PrayerRequest }) => (
        <div className="font-medium text-gray-900">
          {info.row.name}
          {info.row.is_anonymous && (
            <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
              Anônimo
            </span>
          )}
        </div>
      )
    },
    {
      header: 'Pedido',
      accessorKey: 'request',
      cell: (info: { row: PrayerRequest }) => (
        <div className="text-gray-600 line-clamp-2">{info.row.request}</div>
      )
    },
    {
      header: 'Igreja',
      accessorKey: 'igreja_nome',
      enableSorting: true,
    },
    {
      header: 'Status',
      accessorKey: 'status',
      enableSorting: true,
      cell: (info: { row: PrayerRequest }) => {
        let badge;
        
        switch (info.row.status) {
          case 'approved':
            badge = <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">Aprovado</span>;
            break;
          case 'rejected':
            badge = <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">Rejeitado</span>;
            break;
          default:
            badge = <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">Pendente</span>;
        }
        
        return badge;
      }
    },
    {
      header: 'Data',
      accessorKey: 'created_at',
      enableSorting: true,
      cell: (info: { row: PrayerRequest }) => (
        <div>{formatDate(info.row.created_at)}</div>
      )
    },
    {
      header: 'Ações',
      accessorKey: 'id',
      cell: (info: { row: PrayerRequest }) => (
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => {
              setViewingRequest(info.row);
              setIsViewModalOpen(true);
            }}
            className="p-1 text-brand-teal hover:text-brand-teal/80 rounded-full"
            title="Visualizar"
          >
            <Eye className="h-5 w-5" />
          </button>
          {info.row.status === 'pending' && (
            <>
              <button
                onClick={() => handleApprove(info.row)}
                className="p-1 text-green-500 hover:text-green-600 rounded-full"
                title="Aprovar"
              >
                <Check className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleReject(info.row)}
                className="p-1 text-red-500 hover:text-red-600 rounded-full"
                title="Rejeitar"
              >
                <X className="h-5 w-5" />
              </button>
            </>
          )}
          <button
            onClick={() => {
              setRequestToDelete(info.row);
              setIsDeleteModalOpen(true);
            }}
            className="p-1 text-red-500 hover:text-red-600 rounded-full"
            title="Excluir"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      )
    }
  ];

  // Calculate pagination
  const pageCount = Math.ceil(filteredRequests.length / pageSize);
  const paginatedData = filteredRequests.slice(
    pageIndex * pageSize,
    (pageIndex + 1) * pageSize
  );

  return (
    <AdminGlobalLayout title="Pedidos de Oração">
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 text-green-600 rounded-lg flex items-start">
          <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{success}</span>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="igreja_filter" className="block text-sm font-medium text-gray-700 mb-1">
            Filtrar por Igreja
          </label>
          <select
            id="igreja_filter"
            value={selectedIgreja}
            onChange={(e) => setSelectedIgreja(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
          >
            <option value="">Todas as Igrejas</option>
            {igrejas.map((igreja) => (
              <option key={igreja.id} value={igreja.id}>
                {igreja.nome} - {igreja.cidade}/{igreja.estado}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label htmlFor="status_filter" className="block text-sm font-medium text-gray-700 mb-1">
            Filtrar por Status
          </label>
          <select
            id="status_filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
          >
            <option value="">Todos os Status</option>
            <option value="pending">Pendentes</option>
            <option value="approved">Aprovados</option>
            <option value="rejected">Rejeitados</option>
          </select>
        </div>
      </div>

      <AdminDataTable
        data={paginatedData}
        columns={columns}
        title="Pedidos de Oração"
        subtitle={`Total de ${filteredRequests.length} pedidos`}
        loading={loading}
        pagination={{
          pageIndex,
          pageSize,
          pageCount,
          onPageChange: setPageIndex
        }}
        sorting={{
          field: sortField,
          order: sortOrder,
          onSortingChange: handleSortingChange
        }}
        filtering={{
          globalFilter: searchTerm,
          onGlobalFilterChange: setSearchTerm
        }}
        enableExport={true}
        exportFileName="pedidos_oracao"
      />

      {/* View Prayer Request Modal */}
      {isViewModalOpen && viewingRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-xl font-semibold text-brand-teal">Detalhes do Pedido de Oração</h2>
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Nome</h3>
                <p className="mt-1 text-lg font-medium text-gray-900">
                  {viewingRequest.name}
                  {viewingRequest.is_anonymous && (
                    <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                      Anônimo
                    </span>
                  )}
                </p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Igreja</h3>
                <p className="mt-1 text-lg text-gray-900">{viewingRequest.igreja_nome}</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Status</h3>
                <div className="mt-1">
                  {viewingRequest.status === 'approved' ? (
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">Aprovado</span>
                  ) : viewingRequest.status === 'rejected' ? (
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">Rejeitado</span>
                  ) : (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">Pendente</span>
                  )}
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Data</h3>
                <p className="mt-1 text-lg text-gray-900">{formatDate(viewingRequest.created_at)}</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Pedido</h3>
                <div className="mt-2 p-4 bg-gray-50 rounded-lg">
                  <p className="text-gray-900 whitespace-pre-line">{viewingRequest.request}</p>
                </div>
              </div>
              
              <div className="flex justify-between pt-4">
                <div className="flex gap-2">
                  {viewingRequest.status === 'pending' && (
                    <>
                      <button
                        onClick={() => {
                          handleApprove(viewingRequest);
                          setIsViewModalOpen(false);
                        }}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                      >
                        <Check className="h-4 w-4" />
                        Aprovar
                      </button>
                      <button
                        onClick={() => {
                          handleReject(viewingRequest);
                          setIsViewModalOpen(false);
                        }}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
                      >
                        <X className="h-4 w-4" />
                        Rejeitar
                      </button>
                    </>
                  )}
                </div>
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && requestToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmar exclusão</h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir este pedido de oração? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setRequestToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminGlobalLayout>
  );
}